import { createBoard } from './board';
import { PLANT_FAMILIES } from './pieces';
import type { Board, PieceType } from './types';

export interface Challenge {
  id: string;
  title: string;
  description: string;
  target: number;
  metric: 'rows' | 'blooms';
  board: Board;
  pieces?: PieceType[];
  limit?: number;
  water?: boolean;
  seed?: number;
}

function bed(height: number, gaps: number[]): Board {
  const board = createBoard();
  for (let y = 20 - height; y < 20; y++)
    for (let x = 0; x < 10; x++)
      if (!gaps.includes(x)) board[y]![x] = { piece: 'S', family: PLANT_FAMILIES.S };
  return board;
}

const puzzle = (
  id: number,
  title: string,
  description: string,
  height: number,
  gaps: number[],
  pieces: PieceType[],
  target = height,
  water = false,
): Challenge => ({
  id: `puzzle-${id}`,
  title,
  description,
  board: bed(height, gaps),
  pieces,
  target,
  metric: 'rows',
  limit: pieces.length,
  water,
});

/** Small authored beds, ordered from reading the landing guide to planning a sequence. */
export const PUZZLES: readonly Challenge[] = [
  puzzle(
    1,
    'First planting',
    'Slide the long piece into the four spaces. Clear one row.',
    1,
    [3, 4, 5, 6],
    ['I'],
  ),
  puzzle(
    2,
    'A little square',
    'Fit the marigolds into the left-hand bed. Clear two rows.',
    2,
    [0, 1],
    ['O'],
  ),
  puzzle(3, 'Stand tall', 'Turn the hydrangeas upright. Clear all four rows.', 4, [9], ['I']),
  puzzle(
    4,
    'The violet notch',
    'Nestle the pansy into the three spaces. Clear one row.',
    1,
    [4, 5, 6],
    ['T'],
  ),
  puzzle(
    5,
    'Woodland edge',
    'Move the hosta to the left edge. Clear one row.',
    1,
    [0, 1, 2],
    ['J'],
  ),
  puzzle(
    6,
    'Terracotta corner',
    'Find the three spaces on the right. Clear one row.',
    1,
    [7, 8, 9],
    ['L'],
  ),
  puzzle(
    7,
    'Sage step',
    'Line up the lower pair of leaves with the gap. Clear one row.',
    1,
    [3, 4],
    ['S'],
  ),
  puzzle(
    8,
    'Burgundy step',
    'Watch the landing guide: the lower pair fills the gap.',
    1,
    [6, 7],
    ['Z'],
  ),
  puzzle(
    9,
    'Twin planters',
    'Two marigolds, two separate pockets. Clear both rows.',
    2,
    [0, 1, 8, 9],
    ['O', 'O'],
  ),
  puzzle(
    10,
    'Double bloom',
    'Fill the two tall channels. Four rows, just two pieces.',
    4,
    [2, 7],
    ['I', 'I'],
  ),
  puzzle(
    11,
    'A helping hand',
    'Water the bottom row, then stand the hydrangeas in the channel. Clear four rows naturally.',
    5,
    [4],
    ['I'],
    4,
    true,
  ),
  puzzle(
    12,
    'The little courtyard',
    'Plant the square at the left, then fill the four spaces with hydrangeas. Clear one row.',
    1,
    [0, 1, 5, 6, 7, 8],
    ['O', 'I'],
    1,
  ),
];

export function dailyChallenge(date = new Date()): Challenge {
  const day = date.toISOString().slice(0, 10);
  const seed = Number(day.replaceAll('-', ''));
  const kind = seed % 3;
  return {
    id: `daily-${day}`,
    seed,
    metric: kind === 1 ? 'blooms' : 'rows',
    title: kind === 0 ? 'A dozen blooms' : kind === 1 ? 'Full bloom festival' : 'Rescue the garden',
    description:
      kind === 0
        ? 'Clear 12 rows in three minutes.'
        : kind === 1
          ? 'Make three Full Blooms in three minutes.'
          : 'Clear six rows from an overgrown bed in three minutes.',
    target: kind === 0 ? 12 : kind === 1 ? 3 : 6,
    board: kind === 2 ? bed(4, [4, 5]) : createBoard(),
  };
}

export const MODE_NAMES = {
  'growing-season': 'Growing Season',
  'garden-break': 'Garden Break',
  'quiet-garden': 'Quiet Garden',
  'daily-garden': 'Daily Garden',
  'puzzle-garden': 'Garden Puzzles',
} as const;
